import React, { useEffect, useState } from "react";
import { Share2, Copy, ExternalLink, Eye, Users, MessageCircle, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

export default function DealRoomManager({ propertyId }) {
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ show_price: true, show_documents: true, show_owner: false, expires_at: "" });
  const [busy, setBusy] = useState(false);
  const [interests, setInterests] = useState({});
  const publicBase = window.location.origin;

  const load = async () => {
    const { data } = await api.get("/deal-rooms", { params: { property_id: propertyId } });
    setRooms(data);
    for (const r of data) {
      const { data: ints } = await api.get(`/deal-rooms/${r.id}/interests`);
      setInterests((s) => ({ ...s, [r.id]: ints }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/deal-rooms", { property_id: propertyId, ...form, expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null });
      toast.success("Deal Room creata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyUrl = (token) => {
    const url = `${publicBase}/r/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copiato"));
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare questa Deal Room? Il link non funzionerà più.")) return;
    try { await api.delete(`/deal-rooms/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="deal-room-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Share2 className="w-4 h-4" /> {rooms.length} link pubblici
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="gap-2" data-testid="new-room-button"><Plus className="w-4 h-4" /> Nuova Deal Room</Button>
      </div>

      {rooms.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">Nessuna Deal Room. Creane una per condividere l'opportunità in modalità privata.</p>
      ) : (
        <div className="space-y-3">
          {rooms.map((r) => {
            const url = `${publicBase}/r/${r.share_token}`;
            const ints = interests[r.id] || [];
            return (
              <Card key={r.id} className="border-border/60 p-4" data-testid={`room-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium">{r.title}</div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <a href={url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline truncate max-w-full inline-flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> {url}
                      </a>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {r.views_count} visite</span>
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {r.interested_count} interessati</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {r.messages_count} messaggi</span>
                      {r.show_price && <Badge variant="secondary" className="text-[10px]">Prezzo visibile</Badge>}
                      {r.show_documents && <Badge variant="secondary" className="text-[10px]">Doc scaricabili</Badge>}
                      {r.expires_at && <span>Scade: {new Date(r.expires_at).toLocaleDateString("it-IT")}</span>}
                    </div>
                    {ints.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border/60">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Interessati</div>
                        <div className="space-y-1">
                          {ints.slice(0, 3).map((i) => (
                            <div key={i.id} className="text-xs flex items-center gap-2">
                              <span className="font-medium">{i.name || "Anonimo"}</span>
                              <span className="text-muted-foreground">{i.email || i.phone}</span>
                              {i.note && <span className="text-muted-foreground truncate">— {i.note}</span>}
                            </div>
                          ))}
                          {ints.length > 3 && <div className="text-[10px] text-muted-foreground">+{ints.length - 3} altri</div>}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => copyUrl(r.share_token)} className="gap-1" data-testid={`copy-${r.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`del-room-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Deal Room</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between p-3 rounded-md border border-border/60">
              <div><div className="text-sm font-medium">Mostra prezzo</div><div className="text-xs text-muted-foreground">Visibile ai visitatori</div></div>
              <Switch checked={form.show_price} onCheckedChange={(v) => setForm({ ...form, show_price: v })} data-testid="tgl-price" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border/60">
              <div><div className="text-sm font-medium">Documenti scaricabili</div><div className="text-xs text-muted-foreground">I doc caricati sono scaricabili</div></div>
              <Switch checked={form.show_documents} onCheckedChange={(v) => setForm({ ...form, show_documents: v })} data-testid="tgl-docs" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-md border border-border/60">
              <div><div className="text-sm font-medium">Mostra referente (proprietario)</div><div className="text-xs text-muted-foreground">Nome, email, telefono visibili</div></div>
              <Switch checked={form.show_owner} onCheckedChange={(v) => setForm({ ...form, show_owner: v })} data-testid="tgl-owner" />
            </div>
            <div><Label className="text-xs">Scadenza (opzionale)</Label><Input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} data-testid="expires-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={create} disabled={busy} data-testid="create-room-submit">{busy ? "Creazione…" : "Genera link"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
